import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { media, colors } from 'tsx/theme';
import * as Yup from 'yup';
import { Stepper } from './comps';
import { Step1, Step2, Step3, Step4, Step5, Step6 } from './forms';
import { PHONE_REGEX } from 'tsx/util';
import { EmploymentStatus, InterestInstructions } from './types';
import { apiUrl } from 'tsx/util';

const Section = styled.section`
  padding: 0 60px;
  .form {
    margin-top: 37px;
    background-color: ${colors.lp1};
    padding: 70px 160px;

    .input-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 20px;
      > div {
        display: flex;
        flex-direction: column;
        align-items: space-between;
        justify-content: space-between;
      }
    }

    .checkbox-field {
      display: flex;
      gap: 22px;
      grid-gap: 22px;
      margin-bottom: 12px;
    }

    .label {
      display: block;
      color: ${colors.primary};
      font-weight: 600;
      margin-bottom: 8px;
    }

    .sub-text {
      font-style: italic;
      font-weight: 300;

      a {
        color: ${colors.primary};
        font-style: normal;
        font-weight: 600;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  .roll-over {
    margin-top: 37px;
    background-color: ${colors.lp1};
    padding: 70px 160px;
    &__content {
      text-align: center;
      p {
        margin: 36px auto;
        max-width: 70%;
        line-height: 25px;
      }
      .btn {
        margin: 0 auto;
        min-width: 230px;
      }
    }
  }

  .spc30 {
    height: 30px;
  }
  .spc10 {
    height: 10px;
  }

  .buttons {
    display: flex;
    gap: 8px;
    grid-gap: 8px;

    .btn {
      width: 230px !important;
    }
  }

  ${media.lg(css`
    padding: 0 20px;
    .form {
      padding: 50px 60px;
    }
  `)};
  ${media.md(css`
    padding: 0 20px;
    .form {
      padding: 50px 20px;
      .label {
        font-size: 14px;
      }
    }
  `)};
  ${media.sm(css`
    padding: 0;
    .form {
      padding: 20px;
      .input-grid {
        grid-template-columns: 1fr;
      }
      .label {
        font-size: 12px;
      }
    }
    .roll-over {
      padding: 20px 20px;
      &__content {
        p {
          margin-top: 20px;
          margin-bottom: 20px;
          max-width: 100%;
          line-height: 20px;
        }
      }
    }
  `)};
`;
const Div = styled.div`
  background-color: #fff;
  padding: 60px 60px 50px 60px;
  border-radius: 20px;
  position: relative;

  .head {
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
    &:hover {
      transform: scale(1.1);
    }
    transition: transform 0.2s ease-in-out;
  }

  ${media.md(css`
    padding: 40px 30px 20px 30px;
    .head {
      top: 0px;
      right: 0px;
      transform: scale(0.8);
      &:hover {
        transform: scale(0.9);
      }
    }
  `)};
`;

const accountHolderSchema = Yup.object({
  existingCustomer: Yup.boolean().oneOf([true, false], 'Required'),
  title: Yup.object({
    value: Yup.string().required('Required'),
    otherValue: Yup.string()
      .test('conditional-required', 'Required', function () {
        if (this.parent.value === 'Other') {
          return false;
        }
        return true;
      })
      .max(100, 'Max 100 characters'),
  }),

  firstName: Yup.string().required('Required').max(100, 'Max 100 characters'),
  lastName: Yup.string().required('Required').max(100, 'Max 100 characters'),
  contactNumber: Yup.string()
    .required('Required')
    .max(20, 'Max 20 characters')
    .matches(PHONE_REGEX, 'Invalid phone number'),
  alternativeContactNumber: Yup.string()
    .max(20, 'Max 20 characters')
    .matches(PHONE_REGEX, 'Invalid phone number')
    .nullable(),
  emailAddress: Yup.string()
    .required('Required')
    .email('Invalid emailAddress currentAddress')
    .max(100, 'Max 100 characters'),
  nationality: Yup.string().required('Required').max(100, 'Max 100 characters'),
  countryOfBirth: Yup.string().required('Required').max(100, 'Max 100 characters'),
  dateOfBirth: Yup.string().required('Required'),
  currentAddress: Yup.object({
    line1: Yup.string().required('Required').max(200, 'Max 200 characters'),
    line2: Yup.string().nullable().max(200, 'Max 200 characters'),
    city: Yup.string().required('Required').max(200, 'Max 200 characters'),
    county: Yup.string().required('Required').max(200, 'Max 200 characters'),
    postcode: Yup.string().required('Required').max(10, 'Max 10 characters'),
    country: Yup.string().required('Required'),
  }),
  preferredCorrespondence: Yup.string().required('Required'),
  preferredContactTime: Yup.string().required('Required'),
  usCitizen: Yup.object({
    value: Yup.boolean().oneOf([true, false], 'Required'),
    usTaxIdentificationNumber: Yup.string().when('value', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  }),
  taxOtherUK: Yup.object({
    value: Yup.boolean().oneOf([true, false], 'Required'),
    details: Yup.string().when('value', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  }),
  employmentStatus: Yup.object({
    employmentStatus: Yup.string().required('Required'),
    occupation: Yup.string().required('Required').max(200, 'Max 200 characters'),
    annualIncome: Yup.string().required('Required'),

    seniorPositionDetails: Yup.object({
      value: Yup.boolean().oneOf([true, false], 'Required'),
      details: Yup.string().when('value', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    }),
  }),
});

const App = () => {
  const [step, setStep] = useState(1);
  const [isSingleAccount, setIsSingleAccount] = useState(true);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const validationSchema = Yup.object({
    accountOpeningDetails: Yup.object({
      investmentAmount: Yup.number().required('Required').positive('Must be positive'),
      intendedDepositAmount: Yup.number().required('Required').positive('Must be positive'),
      initialDepositSource: Yup.string().required('Required').max(500, 'Max 500 characters'),
      intendedAccountPurpose: Yup.string().required('Required').max(500, 'Max 500 characters'),
      jointAccountDetails: Yup.object({
        accountHoldersRelationship: Yup.string().required('Required').max(500, 'Max 500 characters'),
        withdrawalSignatures: Yup.string().required('Required'),
        correspondenceAddress: Yup.object({
          value: Yup.string().required('Required'),
          otherAddress: Yup.object({
            line1: Yup.string().required('Required').max(200, 'Max 200 characters'),
            line2: Yup.string().nullable().max(200, 'Max 200 characters'),
            city: Yup.string().required('Required').max(200, 'Max 200 characters'),
            county: Yup.string().required('Required').max(200, 'Max 200 characters'),
            postcode: Yup.string().required('Required').max(10, 'Max 10 characters'),
            country: Yup.string().required('Required'),
          }).nullable(),
        }),
      }).nullable(),
      applyToOpenAccount: Yup.boolean().oneOf([true], 'Required'),
    }),

    accountHolderPersonalDetails: accountHolderSchema,

    secondAccountHolderPersonalDetails: accountHolderSchema.nullable(),

    interestRequirement: Yup.object({
      interestInstructions: Yup.object({
        value: Yup.string().required('Required'),
        chorleyAccountNumber: Yup.string().when('value', {
          is: InterestInstructions.PaidToAnotherAccount,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.nullable(),
        }),
        bank: Yup.object({
          nameOfAccountHolder: Yup.string().required('Required'),
          name: Yup.string().required('Required'),
          currentAddress: Yup.string().required('Required'),
          postcode: Yup.string().required('Required'),
          sortcode: Yup.string().required('Required'),
          accountNumber: Yup.number().required('Required'),
        }).when('value', {
          is: InterestInstructions.PaidToBankAccount,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
      declare: Yup.object({
        a: Yup.boolean().oneOf([true], 'Required'),
        b: Yup.boolean().oneOf([true], 'Required'),
        c: Yup.boolean().oneOf([true], 'Required'),
        d: Yup.boolean().oneOf([true], 'Required'),
        e: Yup.boolean().oneOf([true], 'Required'),
        f: Yup.boolean().oneOf([true], 'Required'),

        g: Yup.boolean().oneOf([true], 'Required'),
        h: Yup.boolean().oneOf([true], 'Required'),

        i: Yup.boolean().oneOf([true], 'Required'),
        j: Yup.boolean().oneOf([true], 'Required'),
        k: Yup.boolean().oneOf([true], 'Required'),

        l: Yup.boolean().oneOf([true], 'Required'),
      }),
    }),

    personalInformation: Yup.object({
      applicant1MarketingPreference: Yup.string().required('Required'),
      applicant2MarketingPreference: isSingleAccount ? Yup.string().nullable() : Yup.string().required('Required'),
      isPersonalAccountTransfer: Yup.boolean().oneOf([true], 'Required'),
      wantsToBeContactedByPGMutual: Yup.boolean(),
    }),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      accountOpeningDetails: {
        investmentAmount: '',
        intendedDepositAmount: '',
        initialDepositSource: '',
        intendedAccountPurpose: '',
        jointAccountDetails: null,
        applyToOpenAccount: null,
      },
      accountHolderPersonalDetails: {
        existingCustomer: false,
        title: {
          value: '',
          otherValue: '',
        },
        firstName: '',
        lastName: '',
        contactNumber: '',
        alternativeContactNumber: '',
        emailAddress: '',
        nationality: '',
        countryOfBirth: '',
        dateOfBirth: '',
        currentAddress: {
          line1: '',
          line2: '',
          city: '',
          county: '',
          postcode: '',
          country: '',
        },
        preferredCorrespondence: '',
        preferredContactTime: '',
        usCitizen: {
          value: null,
          usTaxIdentificationNumber: '',
        },
        taxOtherUK: {
          value: null,
          details: '',
        },
        employmentStatus: {
          employmentStatus: '',
          occupation: '',
          annualIncome: '',
          seniorPositionDetails: {
            value: null,
            details: '',
          },
        },
      },
      secondAccountHolderPersonalDetails: null,

      interestRequirement: {
        interestInstructions: {
          value: '',
          chorleyAccountNumber: '',
          bank: null,
        },
        declare: {
          a: false,
          b: false,
          c: false,
          d: false,
          e: false,
          f: false,
          g: false,
          h: false,
          i: false,
          j: false,
          k: false,
          l: false,
        },
      },
      personalInformation: {
        applicant1MarketingPreference: '',
        applicant2MarketingPreference: '',
        isPersonalAccountTransfer: false,
        wantsToBeContactedByPGMutual: false,
      },
    },
    onSubmit: async (values: any) => {
      setIsFormSubmitted(true);

      const { accountOpeningDetails, interestRequirement, personalInformation, ...filteredValues } = values;
      const { applyToOpenAccount, ...filteredAccountOpeningDetails } = accountOpeningDetails || {};
      const { declare, ...filteredDeclaration } = interestRequirement || {};
      const { isPersonalAccountTransfer, ...filteredPersonalInformation } = personalInformation || {};
      const formValue: any = filteredValues;

      formValue.accountHolderPersonalDetails.title = filteredValues.accountHolderPersonalDetails.title.value;

      if (
        filteredValues.secondAccountHolderPersonalDetails !== null &&
        filteredValues.secondAccountHolderPersonalDetails !== undefined
      ) {
        const personalDetails = filteredValues.secondAccountHolderPersonalDetails as any;
        formValue.secondAccountHolderPersonalDetails.title = personalDetails.title?.value ?? '';
      } else {
      }

      formValue.accountHolderPersonalDetails.employmentStatus.seniorPositionDetails =
        formValue.accountHolderPersonalDetails.employmentStatus.seniorPositionDetails.details;

      if (
        formValue.secondAccountHolderPersonalDetails &&
        formValue.secondAccountHolderPersonalDetails.employmentStatus &&
        formValue.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails
      ) {
        formValue.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails =
          formValue.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails.details;
      } else {
      }

      formValue.accountHolderPersonalDetails.taxLiabilities = {
        taxResidentCountries: formValue.accountHolderPersonalDetails.taxOtherUK.details,
        usTaxIdentificationNumber: formValue.accountHolderPersonalDetails.usCitizen.usTaxIdentificationNumber,
      };

      if (formValue.secondAccountHolderPersonalDetails && formValue.secondAccountHolderPersonalDetails.taxOtherUK) {
        formValue.secondAccountHolderPersonalDetails.taxLiabilities = {
          taxResidentCountries: formValue.secondAccountHolderPersonalDetails.taxOtherUK.details ?? '',
          usTaxIdentificationNumber:
            formValue.secondAccountHolderPersonalDetails.usCitizen?.usTaxIdentificationNumber ?? '',
        };
      } else {
      }

      delete formValue.accountHolderPersonalDetails.usCitizen;
      delete formValue.accountHolderPersonalDetails.taxOtherUK;

      if (formValue.secondAccountHolderPersonalDetails && formValue.secondAccountHolderPersonalDetails.usCitizen) {
        delete formValue.secondAccountHolderPersonalDetails.usCitizen;
      } else {
      }
      if (formValue.secondAccountHolderPersonalDetails && formValue.secondAccountHolderPersonalDetails.taxOtherUK) {
        delete formValue.secondAccountHolderPersonalDetails.taxOtherUK;
      } else {
      }

      let modifiedInterestRequirement = {};
      if (values.interestRequirement.interestInstructions.value === 'AddedToAccount') {
        modifiedInterestRequirement = {
          $type: values.interestRequirement.interestInstructions.value,
        };
      } else if (values.interestRequirement.interestInstructions.value === 'PaidToAnotherChorleyAccount') {
        modifiedInterestRequirement = {
          $type: values.interestRequirement.interestInstructions.value,
          accountNumber: values.interestRequirement.interestInstructions.chorleyAccountNumber,
        };
      } else if (values.interestRequirement.interestInstructions.value === 'PaidToABankAccount') {
        modifiedInterestRequirement = {
          $type: values.interestRequirement.interestInstructions.value,
          accountHolderName: values.interestRequirement.interestInstructions.bank.nameOfAccountHolder,
          bankName: values.interestRequirement.interestInstructions.bank.name,
          bankAddress: values.interestRequirement.interestInstructions.bank.currentAddress,
          bankPostcode: values.interestRequirement.interestInstructions.bank.postcode,
          sortCode: values.interestRequirement.interestInstructions.bank.sortcode,
          accountNumber: values.interestRequirement.interestInstructions.bank.accountNumber,
        };
      }

      let modifiedCorrespondenceAddress = {};

      if (
        values.accountOpeningDetails &&
        values.accountOpeningDetails.jointAccountDetails &&
        values.accountOpeningDetails.jointAccountDetails.correspondenceAddress
      ) {
        const correspondenceAddress = values.accountOpeningDetails.jointAccountDetails.correspondenceAddress;
        if (correspondenceAddress.value === 'Applicant1' || correspondenceAddress.value === 'Applicant2') {
          modifiedCorrespondenceAddress = {
            $type: correspondenceAddress.value,
          };
          console.log(correspondenceAddress.value);
        } else if (correspondenceAddress.value === 'Other' && correspondenceAddress.otherAddress) {
          modifiedCorrespondenceAddress = {
            $type: correspondenceAddress.value,
            city: correspondenceAddress.otherAddress.city,
            country: correspondenceAddress.otherAddress.country,
            county: correspondenceAddress.otherAddress.county,
            line1: correspondenceAddress.otherAddress.line1,
            line2: correspondenceAddress.otherAddress.line2,
            postcode: correspondenceAddress.otherAddress.postcode,
          };
        }
      }

      // const requestBody: any = {
      //   ...formValue,
      //   accountOpeningDetails: filteredAccountOpeningDetails,
      //   personalInformation: filteredPersonalInformation,
      //   interestRequirement: {
      //     ...filteredDeclaration,
      //     ...modifiedInterestRequirement,
      //   },
      // };
      const requestBody: any = {
        ...formValue,
        accountOpeningDetails: {
          ...filteredAccountOpeningDetails,
          jointAccountDetails:
            values.accountOpeningDetails.jointAccountDetails &&
            values.accountOpeningDetails.jointAccountDetails.correspondenceAddress
              ? {
                  ...values.accountOpeningDetails.jointAccountDetails,
                  correspondenceAddress: modifiedCorrespondenceAddress,
                }
              : null,
        },
        personalInformation: filteredPersonalInformation,
        interestRequirement: {
          ...filteredDeclaration,
          ...modifiedInterestRequirement,
        },
      };

      delete requestBody.interestRequirement.interestInstructions;

      // console.log('---', requestBody);
      try {
        const response = await fetch(`/api/forms/over-60s`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          console.error('Failed to submit form:', response.status, response.statusText);
          const responseData = await response.json();
          console.error('Response data:', responseData);
          return;
        }
      } catch (error) {
        console.error('An error occurred while submitting the form:', error);
      }
    },
    validateOnMount: true,
  });

  const { values, handleSubmit } = formik;

  useEffect(() => {
    setIsSingleAccount(values.accountOpeningDetails.jointAccountDetails === null);
  }, [values.accountOpeningDetails.jointAccountDetails]);

  const scrollToTop = () => {
    window.scrollTo({ top: 200 });
  };

  function setFormReset() {
    setIsFormSubmitted(false);
    setStep(1);
    formik.resetForm();
    window.location.reload();
  }

  return (
    <Section>
      {!isFormSubmitted ? (
        <div className="form">
          <FormikProvider value={formik}>
            <Stepper step={step} isSingleAccount={isSingleAccount} />
            {step === 1 && <Step1 onNext={() => setStep(2)} />}
            {step === 2 && (
              <Step2
                onNext={() => {
                  setStep(3);
                  scrollToTop();
                }}
                onPrev={() => {
                  setStep(1);
                  scrollToTop();
                }}
              />
            )}
            {step === 3 && (
              <>
                {isSingleAccount ? (
                  <Step4
                    onNext={() => {
                      setStep(4);
                      scrollToTop();
                    }}
                    onPrev={() => {
                      setStep(2);
                      scrollToTop();
                    }}
                  />
                ) : (
                  <Step3
                    onNext={() => {
                      setStep(4);
                      scrollToTop();
                    }}
                    onPrev={() => {
                      setStep(2);
                      scrollToTop();
                    }}
                  />
                )}
              </>
            )}

            {step === 4 && (
              <>
                {isSingleAccount ? (
                  <Step5
                    onNext={() => {
                      setStep(5);
                      scrollToTop();
                    }}
                    onPrev={() => {
                      setStep(3);
                      scrollToTop();
                    }}
                  />
                ) : (
                  <>
                    <Step4
                      onNext={() => {
                        setStep(5);
                        scrollToTop();
                      }}
                      onPrev={() => {
                        setStep(3);
                        scrollToTop();
                      }}
                    />
                  </>
                )}
              </>
            )}

            {step === 5 && (
              <>
                {isSingleAccount ? (
                  <Step6
                    isSingleAccount={isSingleAccount}
                    onSubmit={() => {
                      handleSubmit();
                    }}
                    onStep={(step) => {
                      setStep(step);
                      scrollToTop();
                    }}
                    onPrev={() => {
                      setStep(4);
                      scrollToTop();
                    }}
                  />
                ) : (
                  <Step5
                    onNext={() => {
                      setStep(6);
                      scrollToTop();
                    }}
                    onPrev={() => {
                      setStep(4);
                      scrollToTop();
                    }}
                  />
                )}
              </>
            )}
            {step === 6 && (
              <Step6
                isSingleAccount={false}
                onSubmit={() => {
                  handleSubmit();
                }}
                onStep={(step) => {
                  setStep(step);
                  scrollToTop();
                }}
                onPrev={() => {
                  setStep(4);
                  scrollToTop();
                }}
              />
            )}
          </FormikProvider>
        </div>
      ) : (
        <div className="roll-over">
          <Div>
            <div className="roll-over__content">
              <h4>Submitted</h4>
              <p>Thank you for contacting us.  A member of our team will be in touch soon.</p>

              <button
                className="btn"
                onClick={() => {
                  setFormReset();
                }}
              >
                OK
              </button>
            </div>
          </Div>
        </div>
      )}
    </Section>
  );
};

export default App;
